import {
  DocumentTextIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  ArrowPathIcon,
  ArchiveBoxXMarkIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import Tooltip from "react-tooltip";

export type CardVariant =
  | "total"
  | "active"
  | "inactive"
  | "pending"
  | "expired"
  | "expiring";

export interface BuslicenseAndPermitCardProps {
  variant: CardVariant;
  count: number;
  onClick?: () => void;
}

interface VariantConfig {
  label: string;
  tooltip?: string;
  icon: React.ReactNode;
  iconBgClass: string;
  countColorClass: string;
  labelSizeClass?: string;
}

const VARIANT_CONFIG: Record<CardVariant, VariantConfig> = {
  total: {
    label: "Total",
    icon: <DocumentTextIcon className="h-5 w-5" />,
    iconBgClass: "bg-[#DB2777]",
    countColorClass: "text-[#DB2777]",
  },
  active: {
    label: "Active",
    icon: <CheckCircleIcon className="h-5 w-5" />,
    iconBgClass: "bg-[#1E6FBA]",
    countColorClass: "text-[#1E6FBA]",
  },
  inactive: {
    label: "Inactive",
    icon: <PauseCircleIcon className="h-5 w-5" />,
    iconBgClass: "bg-[#1E6FBA]",
    countColorClass: "text-[#1E6FBA]",
  },
  pending: {
    label: "Pending",
    icon: <ArrowPathIcon className="h-[18px] w-[18px]" />,
    iconBgClass: "bg-[#1E6FBA]",
    countColorClass: "text-[#1E6FBA]",
  },
  expired: {
    label: "Expired",
    icon: <ArchiveBoxXMarkIcon className="h-[18px] w-[18px]" />,
    iconBgClass: "bg-[#1E6FBA]",
    countColorClass: "text-[#1E6FBA]",
  },
  expiring: {
    label: "Expiring",
    tooltip: "Expiring in 45 days",
    icon: <ExclamationCircleIcon className="h-[18px] w-[18px]" />,
    iconBgClass: "bg-[#1E6FBA]",
    countColorClass: "text-[#1E6FBA]",
    labelSizeClass: "text-[15px] 2xl:text-[20px]",
  },
};

const getCountSizeClass = (formattedCount: string): string => {
  const characterCount = formattedCount.length;

  if (characterCount >= 8) {
    return "text-[19px] 2xl:text-[38px]";
  }

  if (characterCount >= 6) {
    return "text-[21px] 2xl:text-[41px]";
  }

  if (characterCount >= 5) {
    return "text-[23px] 2xl:text-[43px]";
  }

  return "text-[25px] 2xl:text-[46px]";
};

const BuslicenseAndPermitCard: React.FC<BuslicenseAndPermitCardProps> = ({
  variant,
  count,
  onClick,
}) => {
  const config = VARIANT_CONFIG[variant];

    const safeCount =
        typeof count === "number" &&
            Number.isFinite(count)
            ? count
            : 0;

    const formattedCount =
        safeCount.toLocaleString();

    const countSizeClass =
        getCountSizeClass(formattedCount);

    const labelSizeClass =
        config.labelSizeClass ??
        "text-[15px] 2xl:text-[20px]";

  const tooltipId = `blp-${variant}-tooltip`;

  const cardClassName = `
    relative
    z-0
    flex
    flex-1
    basis-[180px]
    min-w-[180px]
    flex-col
    overflow-visible
    rounded-xl
    border
    border-[#E7EDF5]
    bg-white
    px-5
    pt-4
    pb-6
    shadow-sm
    transition-all
    duration-200
    ${
      onClick
        ? "cursor-pointer hover:z-20 hover:-translate-y-0.5 hover:shadow-md"
        : ""
    }
  `;

  const cardBody = (
    <>
      <div className="flex w-full items-center">
        <div
          className={[
            "inline-flex",
            "h-10",
            "w-10",
            "shrink-0",
            "items-center",
            "justify-center",
            "rounded-xl",
            "text-white",
            config.iconBgClass,
          ].join(" ")}
        >
          {config.icon}
        </div>
      </div>

      <div
        className="
        mt-4
        grid
        min-w-0
        grid-cols-[minmax(0,1fr)_auto]
        items-center
        gap-x-3 "
      >
        <div className="min-w-0 text-left">
          <div
            className={`
        whitespace-nowrap
        font-bold
        leading-tight
        text-[#153A7B]
        ${labelSizeClass}
        ${config.tooltip ? "cursor-help" : ""}
      `}
            data-tip={config.tooltip}
            data-for={config.tooltip ? tooltipId : undefined}
            title={config.tooltip ? undefined : config.label}
          >
            {config.label}
          </div>

          <div
            className="
        mt-0.5
        whitespace-nowrap
        text-[11px]
        uppercase
        leading-none
        tracking-wide
        text-[#8A9BBE]
        2xl:text-[14px]
      "
          >
            Licenses
          </div>
        </div>

        <div
          className={`flex items-center justify-end self-center whitespace-nowrap text-right font-bold leading-none tabular-nums underline decoration-[2px] underline-offset-4
            ${countSizeClass} ${config.countColorClass}`}
          title={formattedCount}
        >
          {formattedCount}
        </div>
      </div>

      {config.tooltip && (
        <Tooltip
          id={tooltipId}
          place="top"
          effect="solid"
          type="info"
          border={true}
          arrowColor="#0078D7"
          borderColor="#0078D7"
          backgroundColor="white"
          textColor="#0078D7"
        />
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${cardClassName} appearance-none text-left`}
      >
        {cardBody}
      </button>
    );
  }

    return (
        <div className={cardClassName}>
            {cardBody}
        </div>
    );
};

export default BuslicenseAndPermitCard;